import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import centroid from "@turf/centroid";
import along from "@turf/along";
import calculate_length from "@turf/length";

import Modal from "../common_modal/Modal";
import { get_address } from "../../App/actions/propertiesActions";

export default function C_E_ADDRESS({
  config,
  liveValue,
  onClose,
  onSave,
  inPopup = false,
}) {
  const dispatch = useDispatch();
  const [showPopupModal, setShowPopupModal] = useState(false);

  const layer_state = useSelector((state) => state.layer);
  const properties_state = useSelector((state) => state.properties);
  const loading_reducer = useSelector((state) => state.loading_reducer || {});
  const loading_item_array = loading_reducer.loading_item_array || [];

  const [modalInputValue, setModalInputValue] = useState("");

  const initialValue = config?.initialValue;

  // 1. Initial setup when modal first opens
  useEffect(() => {
    if (initialValue !== undefined && initialValue !== null) {
      setModalInputValue(initialValue);
    } else {
      setModalInputValue("");
    }
  }, [initialValue]);

  // 2. Sync when the background API updates the live value in Redux
  useEffect(() => {
    if (liveValue !== undefined && liveValue !== initialValue) {
      setModalInputValue(liveValue);
    }
  }, [liveValue, initialValue]);

  // Fail-safe
  if (!config) return null;

  const { row, field } = config;

  const handleCloseModal = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (inPopup) {
      setShowPopupModal(false);
    } else {
      if (onClose) onClose();
    }
  };

  const handleSaveText = () => {
    onSave(row.id, field.key, modalInputValue, initialValue);
    handleCloseModal();
  };

  const handleUndo = () => {
    const revertedValue = initialValue || "";
    setModalInputValue(revertedValue);
    // Trigger save action with the reverted value but DO NOT close the modal
    onSave(row.id, field.key, revertedValue, initialValue);
  };

  const handleApiGenerate = () => {
    let long;
    let lat;

    if (row?.geometry) {
      const geomType = row.geometry.type;
      const feature = {
        type: "Feature",
        properties: {},
        geometry: row.geometry,
      };

      if (geomType === "Point") {
        long = row.geometry.coordinates[0];
        lat = row.geometry.coordinates[1];
      } else if (geomType === "LineString") {
        const lineLength = calculate_length(feature);
        const midPoint = along(feature, lineLength / 2);
        long = midPoint.geometry.coordinates[0];
        lat = midPoint.geometry.coordinates[1];
      } else {
        const center = centroid(feature);
        long = center.geometry.coordinates[0];
        lat = center.geometry.coordinates[1];
      }
    }

    const body_api = {
      long,
      lat,
      mode: field?.type,
    };

    let body_database;
    let is_nested_call = false;

    if (row?.child_uuid) {
      is_nested_call = true;
      body_database = {
        geo_layer_id: layer_state.layer_id,
        feature_key: properties_state.feature_key_nested_table,
        child_uuid: row.child_uuid,
        properties_key: field.key,
        properties_value: "",
      };
    } else {
      is_nested_call = false;
      body_database = {
        geo_layer_id: layer_state.layer_id,
        feature_key: row?.key,
        properties_key: field.key,
        properties_value: "",
      };
    }

    const body = {
      body_database,
      body_api,
      is_nested: is_nested_call,
      mode: field.type, // Added mode here as per your original code
    };

    dispatch(get_address(body));
    // Modal stays open to watch liveValue update
  };

  // --- REUSABLE RICH CONTENT ---
  const addressDetailsContent = (
    <div
      className="box-body"
      id="box-body"
      onKeyDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="margin_bottom">
        <textarea
          rows={3}
          autoFocus
          className="input_table"
          style={{
            width: "100%",
            padding: "8px",
            border: "1px solid #ccc",
            boxSizing: "border-box",
          }}
          value={modalInputValue}
          onChange={(e) => setModalInputValue(e.target.value)}
          // Textareas shouldn't save on "Enter" so the user can add newlines.
          // They must click "Save Text" to save.
        />

        <p className="text_medium margin_top sub_color margin_bottom_extra">
          Original Value: {initialValue || "-"}
        </p>
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <button className="button background_black" onClick={handleSaveText}>
          Save Text
        </button>

        {loading_item_array.includes("get_address") ? (
          <button className="button background_grey" disabled>
            Memproses ...
          </button>
        ) : (
          <button
            className="button background_black"
            onClick={handleApiGenerate}
            title="Auto-fill using map coordinates via API"
          >
            Generate Address
          </button>
        )}

        {initialValue !== undefined && initialValue !== modalInputValue && (
          <button
            className="button background_grey"
            onClick={handleUndo}
            title="Revert to original value and save"
          >
            Undo
          </button>
        )}

        <button
          className="button background_grey border_black"
          onClick={handleCloseModal}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  if (inPopup) {
    const mode_map = properties_state.mode_map; //editor || viewer
    const displayValue =
      liveValue !== undefined ? liveValue : initialValue || "-";

    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            height: "100%",
          }}
        >
          <p className="container background_white text_medium">
            {displayValue}
          </p>
          {mode_map === "editor" && (
            <button
              className="button background_black"
              onClick={(e) => {
                e.stopPropagation();
                setShowPopupModal(true);
              }}
            >
              Edit
            </button>
          )}
        </div>

        {/* Modal triggered strictly by the popup simplified visual */}
        {showPopupModal && (
          <Modal
            modalSize="small"
            id={`modal-address-${row?.id || "unknown"}-popup`}
            isOpen={true}
            onClose={handleCloseModal}
            title={`Edit ${field.name || field.key}`}
          >
            {addressDetailsContent}
          </Modal>
        )}
      </>
    );
  }

  // 2. STANDARD MODE (Rich Visual directly inside Modal)
  return (
    <Modal
      modalSize="small"
      id={`modal-address-${row?.id || "unknown"}`}
      isOpen={true}
      onClose={handleCloseModal}
      title={`Edit ${field.name || field.key}`}
    >
      {addressDetailsContent}
    </Modal>
  );
}
